import { useState, useEffect, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Assets
import previewIcon from "../../assets/preview.svg";

// Utils
import { DataContext } from "../../utils/DataContext";
import { sortByCustomOrder } from "../../utils/files";

// Components
import Loader, { LOADER_SIZES } from "../../components/Loader";
import SectionDivider from "../../components/SectionDivider";
import CustomButton from "../../components/CustomButton";
import TextDiv from "../../components/TextDiv";
import PageWrapper from "../../components/PageWrapper";
import InformationBox from "../../components/InformationBox";
import { getSupplierUsingId } from "../../services/suppliers/suppliersServices";
import PDFViewer from "../../components/PDFViewer";
import BackToDashboard from "../../components/BackToDashboard";
import useLanguage from "../../utils/useLanguage";

const ViewSupplier = () => {
  const { t } = useTranslation("general");
  const { id } = useParams();
  const { currentDirection } = useLanguage();

  const { error, setError } = useContext(DataContext);

  const [loading, setLoading] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [supplier, setSupplier] = useState({
    name: "",
    company: "",
    country: "",
    city: "",
    phoneNumber: "",
    address: "",
    website: "",
    email: "",
    currency: "",
    countryCode: "",
    businessSector: "",
    typeOfGoods: "",
    relationWithSupplier: "",
    bankAccount: [],
    documents: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getSupplierUsingId(id);

      if (response?.status === 200) {
        setSupplier(response?.data);
      }
    } catch (error) {
      console.error("Error fetching supplier data:", error);
      setError("errorGettingSupplierData");
    } finally {
      setLoading(false);
    }
  };

  const sortedSupplierDocs = useMemo(() => {
    const orderedKeys = ["supplierCertificate", "articleOfAssociation"];
    return sortByCustomOrder(supplier.documents || [], orderedKeys, "name");
  }, [supplier.documents]);

  return (
    <PageWrapper className="add-shipped-container-page" withBackButton>


      <h3>{t("beneficiaryDetails")}</h3>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className={`add-shipped-container-invoice-part`}>
            <SectionDivider title="basicDetails" />

            <TextDiv
              title="supplierName"
              value={supplier?.name || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="companyName"
              value={supplier?.company || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="email"
              value={supplier?.email || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="phoneNumber"
              value={supplier?.phoneNumber || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="countryCode"
              value={supplier?.countryCode || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="website"
              value={supplier?.website || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="currency"
              value={supplier?.currency || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <SectionDivider title="addressDetails" />

            <TextDiv
              title="address"
              value={supplier?.address || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="city"
              value={supplier?.city || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="country"
              value={supplier?.country || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <SectionDivider title="businessInformation" />

            <TextDiv
              title="businessSector"
              value={supplier?.businessSector || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="typeOfGoods"
              value={supplier?.typeOfGoods || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            <TextDiv
              title="relationWithTheSupplier"
              value={supplier?.relationWithSupplier || "-"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            />

            {/* <TextDiv
              title="totalBalance"
              value={supplier?.totalBalanceWithOrg?.toFixed(2) || "0.00"}
              divClasses="item-invoice-div"
              spanClasses="u-flex_flex-2 t-bold_bold"
              pClasses="t-bold_normal u-width--min-width_200"
            /> */}

            <SectionDivider title="bankAccountInfo" />

            {supplier?.bankAccount && supplier.bankAccount.length > 0 ? (
              supplier.bankAccount.map((account, index) => (
                <div key={account.id} className="u-margin--bottom_400">
                  {supplier.bankAccount.length > 1 && (
                    <p className="t-bold_bold u-margin--bottom_200">
                      {t("bankAccount")} {index + 1}
                    </p>
                  )}

                  <TextDiv
                    title="bankName"
                    value={account?.bank?.name || "-"}
                    divClasses="item-invoice-div"
                    spanClasses="u-flex_flex-2 t-bold_bold"
                    pClasses="t-bold_normal u-width--min-width_200"
                  />

                  <TextDiv
                    title="accountNumber"
                    value={account?.accountNumber || "-"}
                    divClasses="item-invoice-div"
                    spanClasses="u-flex_flex-2 t-bold_bold"
                    pClasses="t-bold_normal u-width--min-width_200"
                  />

                  {account?.bank?.branch && account.bank.branch.length > 0 && (
                    <>
                      <TextDiv
                        title="branchName"
                        value={account.bank.branch[0]?.name || "-"}
                        divClasses="item-invoice-div"
                        spanClasses="u-flex_flex-2 t-bold_bold"
                        pClasses="t-bold_normal u-width--min-width_200"
                      />

                      <TextDiv
                        title="branchAddress"
                        value={account.bank.branch[0]?.address || "-"}
                        divClasses="item-invoice-div"
                        spanClasses="u-flex_flex-2 t-bold_bold"
                        pClasses="t-bold_normal u-width--min-width_200"
                      />
                    </>
                  )}
                </div>
              ))
            ) : (
              <InformationBox>{t("noBankAccountsAvailable")}</InformationBox>
            )}
          </div>

          {/* Documents Section */}
          <div className="u-margin--top_700">
            <div className="o-row  o-row--vertical-align_center">
              <SectionDivider title="supplierDocuments" />
              {!showDocuments && (
                <CustomButton
                  variant="blank"
                  title={
                    showDocuments ? t("hideDocuments") : t("showDocuments")
                  }
                  onClick={() => setShowDocuments((prev) => !prev)}
                  icon={previewIcon}
                  iconSize={24}
                />
              )}
            </div>

            {showDocuments && (
              <>
                {loading && <Loader size={LOADER_SIZES.MEDIUM} />}
                {!loading && sortedSupplierDocs?.length === 0 && (
                  <InformationBox>{t("noDocuments")}</InformationBox>
                )}

                {sortedSupplierDocs.map((doc, index) => (
                  <div key={index}>
                    <p className="bold-text">
                      {doc.name === "supplierCertificate"
                        ? t("supplierCertificate")
                        : t("articlesOfAssociation")}
                    </p>
                    <PDFViewer
                      fileId={doc.S3Key}
                      filePath={doc.path}
                      mode="show"
                      fromFile={true}
                    />
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </PageWrapper>
  );
};

export default ViewSupplier;
